/**
 * Invite Email Utilities
 *
 * Utility functions for generating mailto: URLs for inviting attorney clients
 * to use ExpertMD. Supports both single invitations and mass BCC invitations.
 *
 * @module utils/inviteEmailUtils
 */

import * as Sentry from '@sentry/react';

/** Attorney contact info extracted from cases */
export interface AttorneyContact {
  email: string;
  name: string;
  firmName: string;
}

/** Case data with attorney info for extracting contacts */
export interface CaseWithAttorney {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company: string;
}

/**
 * Generate the email subject line for an invitation
 */
export function generateInviteEmailSubject(doctorName: string): string {
  return `Dr. ${doctorName} invites you to connect on ExpertMD`;
}

/**
 * Generate the email body for an invitation
 */
export function generateInviteEmailBody(
  doctorName: string,
  specialty?: string,
  vanityUrl?: string,
): string {
  // Use current origin (staging.expertmd.io or expertmd.io)
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://expertmd.io';
  const intakeUrl = vanityUrl ? `${origin}/${vanityUrl}` : origin;

  const signatureLine = specialty ? `${doctorName}\n${specialty}` : doctorName;

  return `Hi,

I'm using ExpertMD to manage my expert witness cases and would like to invite you to join.

ExpertMD makes it easy to:
- Submit case intake requests directly to me
- Track appointment scheduling
- Share documents securely
- Communicate throughout the case lifecycle

Get started by visiting my intake page:
${intakeUrl}

Best regards,
${signatureLine}`;
}

/**
 * Create a mailto: URL with optional BCC support
 *
 * @param recipients - Email addresses (used as BCC if useBcc is true)
 * @param subject - Email subject line
 * @param body - Email body text
 * @param useBcc - If true, put recipients in BCC field instead of TO
 * @returns Encoded mailto: URL string
 */
export function createMailtoUrl(
  recipients: string[],
  subject: string,
  body: string,
  useBcc: boolean = false,
): string {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  if (useBcc && recipients.length > 0) {
    // For BCC, leave TO empty and put recipients in BCC
    const encodedBcc = recipients.map(encodeURIComponent).join(',');
    return `mailto:?bcc=${encodedBcc}&subject=${encodedSubject}&body=${encodedBody}`;
  } else if (recipients.length > 0) {
    // Single recipient in TO field
    const encodedTo = recipients.map(encodeURIComponent).join(',');
    return `mailto:${encodedTo}?subject=${encodedSubject}&body=${encodedBody}`;
  } else {
    // No recipients
    return `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
  }
}

/**
 * Extract unique attorneys from a list of cases
 * Deduplicates by email address and groups by firm
 */
export function extractUniqueAttorneys(cases: CaseWithAttorney[]): AttorneyContact[] {
  const attorneyMap = new Map<string, AttorneyContact>();

  for (const caseItem of cases) {
    const email = caseItem.email?.toLowerCase().trim();
    if (!email) continue;

    // Skip if we already have this attorney
    if (attorneyMap.has(email)) continue;

    const name =
      [caseItem.firstName, caseItem.lastName].filter(Boolean).join(' ').trim() ||
      'Unknown Attorney';

    attorneyMap.set(email, {
      email,
      name,
      firmName: caseItem.company || 'Unknown Firm',
    });
  }

  // Sort by firm name, then by name
  return Array.from(attorneyMap.values()).sort((a, b) => {
    const firmCompare = a.firmName.localeCompare(b.firmName);
    if (firmCompare !== 0) return firmCompare;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Group attorneys by their firm name
 */
export function groupAttorneysByFirm(attorneys: AttorneyContact[]): Map<string, AttorneyContact[]> {
  const grouped = new Map<string, AttorneyContact[]>();

  for (const attorney of attorneys) {
    const firm = attorney.firmName;
    if (!grouped.has(firm)) {
      grouped.set(firm, []);
    }
    grouped.get(firm)!.push(attorney);
  }

  return grouped;
}

/**
 * Check if the mailto URL might exceed browser limits
 * Most browsers have a ~2000 character limit for URLs
 */
export function checkMailtoUrlLength(url: string): {
  isValid: boolean;
  length: number;
  warning?: string;
} {
  const length = url.length;
  const WARN_THRESHOLD = 1800;
  const MAX_THRESHOLD = 2000;

  if (length > MAX_THRESHOLD) {
    return {
      isValid: false,
      length,
      warning:
        'Too many recipients selected. Some email clients may not support this many addresses. Consider sending in smaller batches.',
    };
  } else if (length > WARN_THRESHOLD) {
    return {
      isValid: true,
      length,
      warning: 'Large number of recipients. Some email clients may truncate the list.',
    };
  }

  return { isValid: true, length };
}

/**
 * Open a mailto URL to trigger the user's default email client
 * Uses anchor element click for better cross-browser compatibility
 */
export function openMailtoUrl(url: string): void {
  // Create a temporary anchor element and click it
  // This is more reliable than window.location.href for mailto links
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Track invite email analytics
 */
export function trackInviteEvent(
  eventName:
    | 'invite_client_modal_opened'
    | 'invite_client_button_clicked'
    | 'invite_email_composed',
  data: Record<string, any>,
): void {
  Sentry.addBreadcrumb({
    category: 'business',
    message: eventName,
    data,
    level: 'info',
  });
}
